import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, Switch } from '@leadsales/design-system/react';
import { useTranslation } from 'react-i18next';
import {
  MAX_OPTION_LABEL,
  type tDraftError,
  type tOptionList,
  type tOptionsDraft,
} from './LeadCloseOptions.functions';
import './LeadCloseOptionsEditor.styles.scss';

const LISTS: tOptionList[] = ['lost', 'resolved'];

type tProps = {
  draft: tOptionsDraft;
  onChange: (draft: tOptionsDraft) => void;
  /** Ids unique per place: the Settings card and the form's dialog can both be open. */
  prefix: string;
  error: tDraftError | null;
  disabled?: boolean;
};

/**
 * The prototype's `optBlock` twice (U17): Motivos de Perdido and Tipos de
 * Resuelto, a 40-character name per option, "Agregar opción" and the "Otro"
 * switch. One editor for the Settings card and the form's "Editar opciones";
 * it only edits a draft, the caller saves it.
 */
export default function LeadCloseOptionsEditor(props: tProps) {
  const { draft, onChange, prefix, error, disabled } = props;
  const { t } = useTranslation('chat/chat');

  const setRows = (type: tOptionList, rows: tOptionsDraft['lost']) =>
    onChange({ ...draft, [type]: rows });

  const focusLater = (id: string) =>
    requestAnimationFrame(() => document.getElementById(id)?.focus());

  return (
    <div className="LeadCloseOptionsEditor">
      {LISTS.map((type) => (
        <section key={type} className="LeadCloseOptionsEditor__Block">
          <h3 className="LeadCloseOptionsEditor__Title">
            {t(`leadClose.settings.lists.${type}.title`)}
          </h3>
          <p className="LeadCloseOptionsEditor__Sub">
            {t(`leadClose.settings.lists.${type}.sub`)}
          </p>
          <div className="LeadCloseOptionsEditor__List">
            {draft[type].map((row, index) => {
              const id = `${prefix}-${type}-${index}`;
              const wrong = error?.type === type && error.index === index;
              return (
                <div
                  key={row.uuid ?? `new-${index}`}
                  className="LeadCloseOptionsEditor__Row"
                >
                  <label className="sr-only" htmlFor={id}>
                    {t('leadClose.settings.optionLabel', { n: index + 1 })}
                  </label>
                  <input
                    id={id}
                    className="FormDialog__Input"
                    // A few over the limit, so a pasted long name gets its sentence (U17).
                    maxLength={MAX_OPTION_LABEL + 10}
                    placeholder={t('leadClose.settings.placeholder')}
                    value={row.label}
                    disabled={disabled}
                    aria-invalid={wrong || undefined}
                    aria-describedby={wrong ? `${prefix}-error` : undefined}
                    onChange={(event) =>
                      setRows(
                        type,
                        draft[type].map((r, i) =>
                          i === index ? { ...r, label: event.target.value } : r,
                        ),
                      )
                    }
                  />
                  <Button
                    type="button"
                    color="ghost"
                    size="sm"
                    iconOnly
                    disabled={disabled}
                    aria-label={t('leadClose.settings.remove', {
                      name:
                        row.label ||
                        t('leadClose.settings.optionLabel', { n: index + 1 }),
                    })}
                    title={t('leadClose.settings.removeTitle')}
                    onClick={() => {
                      setRows(
                        type,
                        draft[type].filter((_, i) => i !== index),
                      );
                      focusLater(`${prefix}-add-${type}`);
                    }}
                  >
                    <TrashIcon width={16} height={16} aria-hidden />
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="LeadCloseOptionsEditor__Foot">
            <Button
              id={`${prefix}-add-${type}`}
              type="button"
              color="tertiary"
              size="sm"
              disabled={disabled}
              onClick={() => {
                setRows(type, [...draft[type], { uuid: null, label: '' }]);
                focusLater(`${prefix}-${type}-${draft[type].length}`);
              }}
            >
              <PlusIcon width={16} height={16} aria-hidden />
              {t('leadClose.settings.add')}
            </Button>
            <Switch
              id={`${prefix}-other-${type}`}
              checked={draft.allowOther[type]}
              disabled={disabled}
              label={t('leadClose.settings.other')}
              description={t(`leadClose.settings.otherHelp.${type}`)}
              onChange={(event) =>
                onChange({
                  ...draft,
                  allowOther: {
                    ...draft.allowOther,
                    [type]: event.target.checked,
                  },
                })
              }
            />
          </div>
        </section>
      ))}
      {error ? (
        <p id={`${prefix}-error`} className="FormDialog__Error" role="alert">
          {t(`leadClose.settings.errors.${error.key}`, {
            max: MAX_OPTION_LABEL,
          })}
        </p>
      ) : null}
    </div>
  );
}
